import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { GuestOpacResourceSummary } from "@/lib/api/guest-opac";
import { canHoverPrefetch, prefetchGuestResource } from "@/lib/guest-opac-prefetch";

interface GuestCatalogCardProps {
  item: GuestOpacResourceSummary;
  tenantSlug?: string;
}

export function GuestCatalogCard({ item, tenantSlug }: GuestCatalogCardProps) {
  const authors = item.authors?.length ? item.authors.join(", ") : "Unknown author";
  const available = item.availability?.availableCopies ?? 0;
  const total = item.availability?.totalCopies ?? 0;

  return (
    <Link
      to={`/opac/view/${item.publicId}`}
      className="group block h-full"
      onMouseEnter={() => {
        if (tenantSlug && canHoverPrefetch()) prefetchGuestResource(item.publicId, tenantSlug);
      }}
    >
      <Card className="h-full overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
        <div className="aspect-[3/4] overflow-hidden bg-muted">
          {item.coverUrl ? (
            <img
              src={item.coverUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <BookOpen className="h-12 w-12 opacity-40" />
            </div>
          )}
        </div>
        <CardContent className="space-y-2 p-4">
          <h3 className="line-clamp-2 text-sm font-semibold text-gray-900 group-hover:text-primary">
            {item.title}
          </h3>
          <p className="line-clamp-1 text-xs text-gray-500">{authors}</p>
          {item.publisher && <p className="line-clamp-1 text-xs text-gray-400">{item.publisher}</p>}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Badge variant="secondary" className="text-[10px] capitalize">
              {item.type}
            </Badge>
            {item.type !== "digital" && total > 0 && (
              <span className="text-[10px] text-gray-500">
                {available > 0 ? `${available} available` : "Unavailable"}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
